import { createClient } from "@supabase/supabase-js";
import { apiServerHeaders } from "@/lib/apiServerHeaders";

// Supabase setup from env variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Direct call API URL
const CALL_SERVICE_URL = import.meta.env.VITE_CALL_SERVICE_URL || "";
const V2_CALL_SERVICE_URL =
  import.meta.env.VITE_V2_CALL_SERVICE_URL ||
  import.meta.env.VITE_V1_CALL_SERVICE_URL ||
  "https://observe-julie-taxi-cas.trycloudflare.com/api/calls";

export type OutboundCallBackend = "v1" | "v2";

interface InitiateCallOptions {
  backend?: OutboundCallBackend;
  /** V2 only: language code the call starts in. Left out when empty (assistant default). */
  startLanguage?: string;
}

const normalizeBaseUrl = (url: string) => url.replace(/\/+$/, "");
const getV2CallsUrl = () => {
  if (CALL_SERVICE_URL) {
    return `${normalizeBaseUrl(CALL_SERVICE_URL)}/api/v2/calls`;
  }

  const normalizedUrl = normalizeBaseUrl(V2_CALL_SERVICE_URL);
  return normalizedUrl.endsWith("/api/calls")
    ? normalizedUrl
    : `${normalizedUrl}/api/calls`;
};

/**
 * Service for making outbound calls through both Supabase functions and direct API
 */
export default {
  /**
   * Initiates a call to an existing lead
   * @param leadId ID of the lead to call
   * @param assistantId ID of the assistant to use for the call
   * @param phoneNumberId ID of the phone number to use for the call
   * @param organizationId Organization ID
   * @param variables Custom variables for the call
   * @returns Call initiation response
   */
  async initiateCallToExistingLead(
    leadId: string,
    assistantId: string,
    phoneNumberId: string | null,
    organizationId: string,
    variables: Record<string, any> = {},
    campaignId?: string,
    isVideo: boolean = false,
    options: InitiateCallOptions = {}
  ) {
    try {
      // First fetch the lead's phone number
      const { data: lead, error: leadError } = await supabase
        .from("leads")
        .select("phone_number")
        .eq("id", leadId)
        .single();

      if (leadError || !lead?.phone_number) {
        throw new Error("Failed to fetch lead's phone number");
      }

      if (options.backend === "v2" && !isVideo) {
        const response = await fetch(
          CALL_SERVICE_URL ? `${CALL_SERVICE_URL}/api/call` : getV2CallsUrl(),
          {
            method: "POST",
            headers: await apiServerHeaders(),
            body: JSON.stringify(CALL_SERVICE_URL ? {
              backend: "v2",
              lead_id: leadId,
              assistant_id: assistantId,
              agent_id: assistantId,
              organization_id: organizationId,
              variables,
              prompt_variables: variables,
              ...(campaignId ? { campaign_id: campaignId } : {}),
              ...(options.startLanguage ? { start_language: options.startLanguage } : {}),
            } : {
              to: lead.phone_number,
              agent_id: assistantId,
              prompt_variables: variables,
            }),
          }
        );

        const result = await response.json().catch(() => ({}));

        if (!response.ok || result.error || result.detail) {
          throw new Error(
            result.detail ||
              result.error ||
              "Failed to initiate the call through V2 backend"
          );
        }

        return {
          ...result,
          success: result.success ?? true,
          backend: "v2",
        };
      }

      // Try using the direct API if URL is provided
      if (CALL_SERVICE_URL) {
        const requestBody: any = {
          lead_id: leadId,
          assistant_id: assistantId,
          phone_number_id: phoneNumberId,
          organization_id: organizationId,
          variables: variables,
          phoneNumber: lead.phone_number, // Include phone number in payload
          isVideo: isVideo // Include video flag
        };

        // Add campaign_id if provided
        if (campaignId) {
          requestBody.campaign_id = campaignId;
        }

        const response = await fetch(`${CALL_SERVICE_URL}/api/call`, {
          method: "POST",
          headers: await apiServerHeaders(),
          body: JSON.stringify(requestBody),
        });

        if (response.ok) {
          const result = await response.json();
          // Check if the successful response contains an error
          if (result.error || result.detail) {
            throw new Error(result.detail || result.error || "Call failed with unknown error");
          }
          return result;
        }
        
        // Parse error details from API response
        let errorMessage = "Failed to initiate the call. Please try again.";
        try {
          const errorResponse = await response.json();
          if (errorResponse.detail) {
            errorMessage = errorResponse.detail;
          }
        } catch (parseError) {
          console.warn("Could not parse error response:", parseError);
        }
        
        // If it's a client error (400-499), don't fall back - throw the specific error
        if (response.status >= 400 && response.status < 500) {
          throw new Error(errorMessage);
        }
        
        // For server errors (500+), fall back to Supabase function
        console.warn(
          "Direct API call failed with server error, falling back to Supabase function"
        );
      }

      // Fall back to using Supabase function
      const requestBody: any = {
        leadId: leadId,
        assistantId: assistantId,
        sourcePhoneNumberId: phoneNumberId,
        organizationId: organizationId,
        variableValues: variables,
        phoneNumber: lead.phone_number, // Include phone number in payload
        isVideo: isVideo // Include video flag
      };

      // Add campaign_id if provided
      if (campaignId) {
        requestBody.campaignId = campaignId;
      }

      const { data, error } = await supabase.functions.invoke("initiate_call", {
        body: requestBody,
      });

      if (error) {
        // Try to extract meaningful error message from Supabase function error
        let errorMessage = "Call service not fully configured";
        if (error.message) {
          errorMessage = error.message;
        }
        throw new Error(errorMessage);
      }
      
      // Check if the successful response contains an error
      if (data && (data.error || data.detail)) {
        throw new Error(data.detail || data.error || "Call failed with unknown error");
      }
      
      return data;
    } catch (error) {
      console.error("Error initiating call:", error);
      throw error;
    }
  },

  /**
   * Creates a new lead and initiates a call to it
   * @param leadData Lead information
   * @param assistantId ID of the assistant to use for the call
   * @param phoneNumberId ID of the phone number to use for the call
   * @param organizationId Organization ID
   * @param variables Custom variables for the call
   * @returns Call initiation response
   */
  async initiateCallToNewLead(
    leadData: {
      first_name: string;
      last_name: string;
      phone_number: string;
      email?: string;
      company?: string;
      title?: string;
      notes?: string;
    },
    assistantId: string,
    phoneNumberId: string | null,
    organizationId: string,
    variables: Record<string, any> = {},
    isVideo: boolean = false,
    options: InitiateCallOptions = {}
  ) {
    try {
      if (options.backend === "v2" && !isVideo) {
        const response = await fetch(
          CALL_SERVICE_URL ? `${CALL_SERVICE_URL}/api/call` : getV2CallsUrl(),
          {
            method: "POST",
            headers: await apiServerHeaders(),
            body: JSON.stringify(CALL_SERVICE_URL ? {
              backend: "v2",
              lead: leadData,
              assistant_id: assistantId,
              agent_id: assistantId,
              organization_id: organizationId,
              variables,
              prompt_variables: variables,
              ...(options.startLanguage ? { start_language: options.startLanguage } : {}),
            } : {
              to: leadData.phone_number,
              agent_id: assistantId,
              prompt_variables: variables,
            }),
          }
        );

        const result = await response.json().catch(() => ({}));

        if (!response.ok || result.error || result.detail) {
          throw new Error(
            result.detail ||
              result.error ||
              "Failed to initiate the call through V2 backend"
          );
        }

        return {
          ...result,
          success: result.success ?? true,
          backend: "v2",
        };
      }

      // Try using the direct API if URL is provided
      if (CALL_SERVICE_URL) {
        const response = await fetch(`${CALL_SERVICE_URL}/api/call`, {
          method: "POST",
          headers: await apiServerHeaders(),
          body: JSON.stringify({
            lead: leadData,
            assistant_id: assistantId,
            phone_number_id: phoneNumberId,
            organization_id: organizationId,
            variables: variables,
            phoneNumber: leadData.phone_number, // Include phone number in payload
            isVideo: isVideo // Include video flag
          }),
        });

        if (response.ok) {
          const result = await response.json();
          // Check if the successful response contains an error
          if (result.error || result.detail) {
            throw new Error(result.detail || result.error || "Call failed with unknown error");
          }
          return result;
        }
        
        // Parse error details from API response
        let errorMessage = "Failed to initiate the call. Please try again.";
        try {
          const errorResponse = await response.json();
          if (errorResponse.detail) {
            errorMessage = errorResponse.detail;
          }
        } catch (parseError) {
          console.warn("Could not parse error response:", parseError);
        }
        
        // If it's a client error (400-499), don't fall back - throw the specific error
        if (response.status >= 400 && response.status < 500) {
          throw new Error(errorMessage);
        }
        
        // For server errors (500+), fall back to creating lead manually
        console.warn(
          "Direct API call failed with server error, falling back to manual lead creation and Supabase function"
        );
      }

      // Create lead first
      const { data: leadResult, error: leadError } = await supabase
        .from("leads")
        .insert([
          {
            first_name: leadData.first_name,
            last_name: leadData.last_name,
            phone_number: leadData.phone_number,
            email: leadData.email || null,
            company: leadData.company || null,
            title: leadData.title || null,
            notes: leadData.notes || null,
            organization_id: organizationId,
            status: "new",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          },
        ])
        .select();

      if (leadError) throw leadError;

      const newLeadId = leadResult?.[0]?.id;
      if (!newLeadId) throw new Error("Failed to create lead");

      // Now initiate call using the new lead ID
      return await this.initiateCallToExistingLead(
        newLeadId,
        assistantId,
        phoneNumberId,
        organizationId,
        variables,
        undefined, // campaignId
        isVideo,   // pass the video flag
        options
      );
    } catch (error) {
      console.error("Error creating lead and initiating call:", error);
      throw error;
    }
  },
};
